import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import showToast from '@/components/ui/Toast';
import { addHost, listHosts, removeHost } from '@/services/communityHostService';
import {
  communityHostKeys,
  type HostCandidate,
  type HostListItem,
} from '@/types/communityHost';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { HostManagementHeaderStats } from '@/components/community/host-management/HostManagementHeaderStats';
import { HostList } from '@/components/community/host-management/HostList';
import { HostActionsSheet } from '@/components/community/host-management/HostActionsSheet';
import { AddCoHostSheet } from '@/components/community/host-management/AddCoHostSheet';
import { HostConfirmDialog, HostInfoDialog } from '@/components/community/host-management/HostDialogs';
import { HostMemberProfileSheet } from '@/components/community/host-management/HostMemberProfileSheet';

export function HostManagementPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const slug = user?.slug || (typeof localStorage !== 'undefined' ? localStorage.getItem('communityNickname') || '' : '');

  const [addOpen, setAddOpen] = useState(false);
  const [actionHost, setActionHost] = useState<HostListItem | null>(null);
  const [profileHost, setProfileHost] = useState<HostListItem | null>(null);
  const [ownerHost, setOwnerHost] = useState<HostListItem | null>(null);
  const [notMemberHost, setNotMemberHost] = useState<HostListItem | null>(null);
  const [confirmAdd, setConfirmAdd] = useState<HostCandidate | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<HostListItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: communityHostKeys.list,
    queryFn: listHosts,
  });

  const invalidateHosts = async () => {
    await queryClient.invalidateQueries({ queryKey: communityHostKeys.list });
    await queryClient.invalidateQueries({ queryKey: ['community-host-candidates'] });
  };

  const addMutation = useMutation({
    mutationFn: (communityUserId: number) => addHost(communityUserId),
    onSuccess: async () => {
      showToast.success(`${confirmAdd?.name ?? 'Member'} diangkat menjadi co-host`);
      setConfirmAdd(null);
      await invalidateHosts();
    },
    onError: (error) => {
      showToast.error(getApiErrorMessage(error, 'Gagal mengangkat co-host'));
    },
  });

  const removeMutation = useMutation({
    mutationFn: (communityUserId: number) => removeHost(communityUserId),
    onSuccess: async () => {
      showToast.success(`Akses co-host ${confirmRemove?.name ?? ''} dicabut`);
      setConfirmRemove(null);
      await invalidateHosts();
    },
    onError: (error) => {
      showToast.error(getApiErrorMessage(error, 'Gagal mencabut akses co-host'));
    },
  });

  const handleKebab = (host: HostListItem) => {
    if (host.kind === 'owner') {
      setOwnerHost(host);
      return;
    }
    setActionHost(host);
  };

  const handleViewProfile = (host: HostListItem) => {
    setActionHost(null);
    if (!host.is_member || !host.member_id) {
      setNotMemberHost(host);
      return;
    }
    setProfileHost(host);
  };

  const hosts = data?.hosts ?? [];

  return (
    <div className="min-h-full bg-[#F8FAFC] px-4 pt-3 pb-8 md:px-0 md:pt-0">
      <div className="hidden md:block mb-6">
        <h1 className="text-2xl font-extrabold text-[#0E1726]">Host Management</h1>
        <p className="text-xs text-gray-400 font-medium mt-1">
          Player dengan akses kelola komunitas
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        <HostManagementHeaderStats stats={data?.stats} />
        {isLoading ? (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-8 text-center text-xs text-gray-400">
            Memuat pengelola...
          </div>
        ) : (
          <HostList hosts={hosts} onOpenActions={handleKebab} />
        )}
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="w-full h-12 rounded-full bg-[#FC480D] hover:bg-[#e03e09] text-white text-sm font-extrabold cursor-pointer shadow-sm"
        >
          Tambah Co-Host
        </button>
      </div>

      <AddCoHostSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSelect={setConfirmAdd}
      />
      <HostActionsSheet
        host={actionHost}
        onClose={() => setActionHost(null)}
        onViewProfile={handleViewProfile}
        onRemove={(host) => {
          setActionHost(null);
          setConfirmRemove(host);
        }}
      />
      <HostMemberProfileSheet host={profileHost} slug={slug} onClose={() => setProfileHost(null)} />
      <HostInfoDialog
        open={!!ownerHost}
        title={ownerHost?.name ?? ''}
        icon="ph:crown-simple-bold"
        message={'Owner tidak bisa diubah aksesnya.\nPindahkan kepemilikan dulu kalau perlu.'}
        onClose={() => setOwnerHost(null)}
      />
      <HostInfoDialog
        open={!!notMemberHost}
        title={notMemberHost?.name ?? ''}
        icon="ph:user-circle-bold"
        message="Co-host ini belum terhubung ke profil member komunitas."
        onClose={() => setNotMemberHost(null)}
      />
      <HostConfirmDialog
        open={!!confirmAdd}
        title="Angkat Co-Host"
        message={`Angkat ${confirmAdd?.name ?? ''} sebagai co-host?`}
        confirmLabel="Angkat"
        loading={addMutation.isPending}
        onCancel={() => setConfirmAdd(null)}
        onConfirm={() => confirmAdd && addMutation.mutate(confirmAdd.community_user_id)}
      />
      <HostConfirmDialog
        open={!!confirmRemove}
        title="Cabut Akses"
        message={`Cabut akses co-host ${confirmRemove?.name ?? ''}?`}
        confirmLabel="Cabut Akses"
        danger
        loading={removeMutation.isPending}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => confirmRemove && removeMutation.mutate(confirmRemove.community_user_id)}
      />
    </div>
  );
};

export default HostManagementPage;
